import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Clock, Package, Sparkles, CalendarDays, CheckCircle2, Loader2, Palette, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { Reveal } from "@/components/Reveal";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";

const EVENT_TYPES = ["Birthday Party", "Market / Festival / Fair", "School / Community Event", "Corporate / Special Event", "Wedding", "Holiday Celebration", "Fundraiser", "Private Party", "Other"];

const fmtDate = (d) => {
  if (!d) return null;
  const y = d.getFullYear(); const m = String(d.getMonth() + 1).padStart(2, "0"); const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

function TabButton({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`flex-1 min-w-[9rem] flex items-center justify-center gap-2 px-4 py-3 rounded-2xl border-[3px] border-ink font-heading font-600 transition ${active ? "bg-hotpink text-white" : "bg-white text-ink hover:bg-sunshine"}`}>
      <Icon className="w-5 h-5" /> {label}
    </button>
  );
}

function SlotPicker({ dateStr, duration, value, onChange }) {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!dateStr) { setSlots([]); setInfo(null); return; }
    setLoading(true);
    api.get("/availability/slots", { params: { date: dateStr, duration } })
      .then((r) => { setSlots(r.data.slots || []); setInfo(r.data.available ? null : (r.data.reason || "none")); })
      .catch(() => { setSlots([]); setInfo("error"); })
      .finally(() => setLoading(false));
  }, [dateStr, duration]);

  if (!dateStr) return <p className="text-ink/60">Pick a date to see available start times.</p>;
  if (loading) return <p className="flex items-center gap-2 text-ink/70"><Loader2 className="w-4 h-4 animate-spin" /> Checking availability…</p>;
  if (slots.length === 0) return <p className="text-hotpink font-600" data-testid="no-slots">No available times for this date. Please choose another day.</p>;

  return (
    <div className="flex flex-wrap gap-2" data-testid="slot-list">
      {slots.map((s) => (
        <button key={s} type="button" onClick={() => onChange(s)} data-testid={`slot-${s}`}
          className={`px-4 py-2 rounded-full border-[2.5px] border-ink font-600 ${value === s ? "bg-brightblue text-ink" : "bg-white hover:bg-sunshine"}`}>
          {s}
        </button>
      ))}
    </div>
  );
}

export default function BookNow() {
  useSeo({ title: "Book Now — Face Painting in Nanaimo", description: "Book Paint With Tay for your event. Choose hourly, a package, or request a custom quote — quick and easy online booking." });
  const [params] = useSearchParams();
  const initialTab = params.get("type") === "custom" ? "custom" : "hourly";
  const [tab, setTab] = useState(initialTab);
  const [settings, setSettings] = useState({});
  const [packages, setPackages] = useState([]);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get("/settings/public").then((r) => setSettings(r.data || {})).catch(() => {});
    api.get("/packages").then((r) => setPackages(r.data || [])).catch(() => {});
  }, []);

  const minDate = useMemo(() => {
    const d = new Date(); d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + (settings.min_advance_days || 0));
    return d;
  }, [settings]);

  if (done) return <Confirmation done={done} onReset={() => setDone(null)} />;

  return (
    <div data-testid="book-page">
      <section className="section pt-12 pb-6 text-center">
        <Reveal>
          <p className="font-accent text-3xl text-hotpink">Let’s add colour to your celebration</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-700 mb-3">Book Paint With Tay</h1>
          <p className="max-w-2xl mx-auto text-ink/80 text-lg">Requests are reviewed by Tay and confirmed by email — your chosen time is held while she takes a look. No online payment needed to request.</p>
        </Reveal>
      </section>

      <section className="section pb-16 max-w-4xl">
        <div className="flex flex-wrap gap-3 mb-6">
          <TabButton active={tab === "hourly"} onClick={() => setTab("hourly")} icon={Clock} label="Hourly Booking" testid="tab-hourly" />
          <TabButton active={tab === "package"} onClick={() => setTab("package")} icon={Package} label="Event Package" testid="tab-package" />
          <TabButton active={tab === "custom"} onClick={() => setTab("custom")} icon={Sparkles} label="Custom Request" testid="tab-custom" />
        </div>

        {tab === "hourly" && <HourlyFlow settings={settings} minDate={minDate} onDone={setDone} />}
        {tab === "package" && <PackageFlow packages={packages} minDate={minDate} onDone={setDone} />}
        {tab === "custom" && <CustomFlow onDone={setDone} />}
      </section>
    </div>
  );
}

function TermsCheck({ checked, onChange }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <Checkbox checked={checked} onCheckedChange={onChange} className="mt-1 border-[2.5px] border-ink data-[state=checked]:bg-hotpink" data-testid="terms-checkbox" />
      <span className="text-sm text-ink/80">I agree to the <Link to="/terms" className="text-hotpink font-700 underline" target="_blank">Booking Terms & Cancellation Policy</Link>. I understand this is a request and my booking is confirmed once Tay approves it.</span>
    </label>
  );
}

function ContactFields({ form, set }) {
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <div><label className="label">Your name *</label><input className="field" value={form.name} onChange={set("name")} data-testid="field-name" /></div>
      <div><label className="label">Email *</label><input type="email" className="field" value={form.email} onChange={set("email")} data-testid="field-email" /></div>
      <div><label className="label">Phone</label><input className="field" value={form.phone} onChange={set("phone")} data-testid="field-phone" /></div>
    </div>
  );
}

function HourlyFlow({ settings, minDate, onDone }) {
  const [date, setDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    event_type: "", duration_hours: settings.min_hours || 1.5, start_time: "",
    location: "", guests: "", faces_expected: "", theme: "", special_instructions: "",
    name: "", email: "", phone: "", agreed_to_terms: false,
  });
  useEffect(() => { setForm((f) => ({ ...f, duration_hours: settings.min_hours || 1.5 })); }, [settings.min_hours]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const dateStr = fmtDate(date);

  const price = settings.hourly_enabled && settings.hourly_rate ? Number(form.duration_hours) * settings.hourly_rate : null;
  const durations = [];
  for (let h = settings.min_hours || 1.5; h <= (settings.max_hours || 6) + 1e-9; h += 0.5) durations.push(Math.round(h * 2) / 2);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.event_type) return toast.error("Please choose an event type.");
    if (!dateStr || !form.start_time) return toast.error("Please pick a date and start time.");
    if (!form.name || !form.email) return toast.error("Please add your name and email.");
    if (!form.agreed_to_terms) return toast.error("Please agree to the booking terms.");
    setSubmitting(true);
    try {
      const res = await api.post("/bookings", {
        booking_type: "hourly", ...form,
        guests: form.guests ? Number(form.guests) : null,
        faces_expected: form.faces_expected ? Number(form.faces_expected) : null,
        date: dateStr, estimated_price: price,
      });
      onDone({ id: res.data.id, date: dateStr, start_time: form.start_time, duration: form.duration_hours, name: form.name, type: "Hourly booking" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Try another time.");
    } finally { setSubmitting(false); }
  };

  return (
    <form onSubmit={submit} className="card-pop p-6 sm:p-8 space-y-6" data-testid="hourly-form">
      <div>
        <label className="label">Event type *</label>
        <select className="field" value={form.event_type} onChange={set("event_type")} data-testid="hourly-event-type">
          <option value="">Choose one…</option>
          {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="label"><CalendarDays className="inline w-4 h-4 mr-1" /> Choose a date *</label>
          <div className="card-soft p-2 inline-block">
            <Calendar mode="single" selected={date} onSelect={(d) => { setDate(d); setForm((f) => ({ ...f, start_time: "" })); }}
              disabled={(d) => d < minDate} data-testid="hourly-calendar" />
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <label className="label">How many hours? *</label>
            <select className="field" value={form.duration_hours} onChange={(e) => { set("duration_hours")(e); setForm((f) => ({ ...f, duration_hours: e.target.value, start_time: "" })); }} data-testid="hourly-duration">
              {durations.map((h) => <option key={h} value={h}>{h} {h === 1 ? "hour" : "hours"}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Available start times *</label>
            <SlotPicker dateStr={dateStr} duration={Number(form.duration_hours)} value={form.start_time} onChange={(s) => setForm({ ...form, start_time: s })} />
          </div>
          {price != null && (
            <div className="card-soft p-4 bg-sunshine/25" data-testid="hourly-price">
              <p className="text-sm text-ink/70">Estimated price</p>
              <p className="font-heading text-2xl font-700">${price.toFixed(0)} <span className="text-sm font-body text-ink/60">({form.duration_hours}h × ${settings.hourly_rate}/h)</span></p>
              <p className="text-xs text-ink/60 mt-1">Final quote confirmed by Tay. A $50 travel fee applies for events more than 30 km from Nanaimo.</p>
            </div>
          )}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div><label className="label">Event location *</label><input className="field" value={form.location} onChange={set("location")} placeholder="Address or venue in Nanaimo area" data-testid="hourly-location" /></div>
        <div><label className="label">Guests who may want painting</label><input type="number" min="0" className="field" value={form.guests} onChange={set("guests")} data-testid="hourly-guests" /></div>
        <div><label className="label">Expected number of faces</label><input type="number" min="0" className="field" value={form.faces_expected} onChange={set("faces_expected")} data-testid="hourly-faces" /></div>
        <div><label className="label">Event theme</label><input className="field" value={form.theme} onChange={set("theme")} placeholder="e.g. jungle animals, superheroes" data-testid="hourly-theme" /></div>
      </div>
      <div><label className="label">Special instructions</label><textarea rows={3} className="field" value={form.special_instructions} onChange={set("special_instructions")} data-testid="hourly-notes" /></div>

      <ContactFields form={form} set={set} />
      <TermsCheck checked={form.agreed_to_terms} onChange={(v) => setForm({ ...form, agreed_to_terms: !!v })} />
      <button type="submit" disabled={submitting} className="btn-pink w-full text-lg disabled:opacity-60" data-testid="hourly-submit">
        {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Palette className="w-5 h-5" />} Review & submit request
      </button>
    </form>
  );
}

function PackageFlow({ packages, minDate, onDone }) {
  const [selected, setSelected] = useState(null);
  const [date, setDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ start_time: "", location: "", theme: "", special_instructions: "", name: "", email: "", phone: "", addons: [], agreed_to_terms: false });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const dateStr = fmtDate(date);
  const pkg = packages.find((p) => p.id === selected);

  const toggleAddon = (name) => setForm((f) => ({ ...f, addons: f.addons.includes(name) ? f.addons.filter((a) => a !== name) : [...f.addons, name] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!pkg) return toast.error("Please choose a package.");
    if (!dateStr || !form.start_time) return toast.error("Please pick a date and start time.");
    if (!form.name || !form.email) return toast.error("Please add your name and email.");
    if (!form.agreed_to_terms) return toast.error("Please agree to the booking terms.");
    setSubmitting(true);
    try {
      const res = await api.post("/bookings", {
        booking_type: "package", package_id: pkg.id, event_type: "Package: " + pkg.name,
        date: dateStr, start_time: form.start_time, duration_hours: pkg.duration_hours,
        location: form.location, theme: form.theme, special_instructions: form.special_instructions,
        addons: form.addons, name: form.name, email: form.email, phone: form.phone,
        agreed_to_terms: true, estimated_price: pkg.base_price,
      });
      onDone({ id: res.data.id, date: dateStr, start_time: form.start_time, duration: pkg.duration_hours, name: form.name, type: `Package — ${pkg.name}` });
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not submit. Try another time."); }
    finally { setSubmitting(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="package-form">
      <div className="grid md:grid-cols-3 gap-4">
        {packages.map((p) => (
          <button type="button" key={p.id} onClick={() => { setSelected(p.id); setForm((f) => ({ ...f, start_time: "" })); }}
            data-testid={`package-${p.id}`}
            className={`text-left card-soft p-5 ${selected === p.id ? "!border-hotpink ring-4 ring-sunshine" : ""}`}>
            <h3 className="font-heading text-xl font-700">{p.name}</h3>
            <p className="font-heading text-2xl text-hotpink my-1">${p.base_price}</p>
            <p className="text-sm text-ink/75 mb-2">{p.description}</p>
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="chip">{p.duration_hours}h</span>
              {p.max_faces ? <span className="chip">~{p.max_faces} faces</span> : null}
            </div>
          </button>
        ))}
      </div>

      {pkg && (
        <div className="card-pop p-6 sm:p-8 space-y-6">
          {pkg.addons?.length > 0 && (
            <div>
              <label className="label">Optional add-ons</label>
              <div className="flex flex-wrap gap-2">
                {pkg.addons.map((a) => (
                  <button type="button" key={a.name} onClick={() => toggleAddon(a.name)} data-testid={`addon-${a.name}`}
                    className={`px-4 py-2 rounded-full border-[2.5px] border-ink font-600 ${form.addons.includes(a.name) ? "bg-leaf text-white" : "bg-white hover:bg-sunshine"}`}>
                    {a.name}{a.price ? ` (+$${a.price})` : ""}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="label"><CalendarDays className="inline w-4 h-4 mr-1" /> Choose a date *</label>
              <div className="card-soft p-2 inline-block">
                <Calendar mode="single" selected={date} onSelect={(d) => { setDate(d); setForm((f) => ({ ...f, start_time: "" })); }} disabled={(d) => d < minDate} data-testid="package-calendar" />
              </div>
            </div>
            <div>
              <label className="label">Available start times *</label>
              <SlotPicker dateStr={dateStr} duration={pkg.duration_hours} value={form.start_time} onChange={(s) => setForm({ ...form, start_time: s })} />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="label">Event location *</label><input className="field" value={form.location} onChange={set("location")} data-testid="package-location" /></div>
            <div><label className="label">Event theme</label><input className="field" value={form.theme} onChange={set("theme")} data-testid="package-theme" /></div>
          </div>
          <div><label className="label">Special instructions</label><textarea rows={3} className="field" value={form.special_instructions} onChange={set("special_instructions")} data-testid="package-notes" /></div>
          <ContactFields form={form} set={set} />
          <TermsCheck checked={form.agreed_to_terms} onChange={(v) => setForm({ ...form, agreed_to_terms: !!v })} />
          <button type="submit" disabled={submitting} className="btn-pink w-full text-lg disabled:opacity-60" data-testid="package-submit">
            {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Package className="w-5 h-5" />} Submit package request
          </button>
        </div>
      )}
    </form>
  );
}

function CustomFlow({ onDone }) {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "", organization: "", email: "", phone: "", event_type: "", date: "", start_time: "", end_time: "",
    location: "", guests: "", faces_expected: "", indoor_outdoor: "", theme: "", budget: "", special_instructions: "", agreed_to_terms: false,
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return toast.error("Please add your name and email.");
    if (!form.event_type) return toast.error("Please choose an event type.");
    if (!form.agreed_to_terms) return toast.error("Please agree to the booking terms.");
    setSubmitting(true);
    try {
      const res = await api.post("/bookings", {
        booking_type: "custom", ...form,
        guests: form.guests ? Number(form.guests) : null,
        faces_expected: form.faces_expected ? Number(form.faces_expected) : null,
      });
      onDone({ id: res.data.id, name: form.name, type: "Custom quote request", date: form.date });
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not submit your request."); }
    finally { setSubmitting(false); }
  };

  return (
    <form onSubmit={submit} className="card-pop p-6 sm:p-8 space-y-5" data-testid="custom-form">
      <p className="text-ink/70">For schools, festivals, markets, corporate events and larger or unusual bookings. Share the details and Tay will send a custom quote.</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <div><label className="label">Name *</label><input className="field" value={form.name} onChange={set("name")} data-testid="custom-name" /></div>
        <div><label className="label">Organization</label><input className="field" value={form.organization} onChange={set("organization")} data-testid="custom-org" /></div>
        <div><label className="label">Email *</label><input type="email" className="field" value={form.email} onChange={set("email")} data-testid="custom-email" /></div>
        <div><label className="label">Phone</label><input className="field" value={form.phone} onChange={set("phone")} data-testid="custom-phone" /></div>
        <div>
          <label className="label">Event type *</label>
          <select className="field" value={form.event_type} onChange={set("event_type")} data-testid="custom-event-type">
            <option value="">Choose one…</option>
            {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div><label className="label">Event date</label><input type="date" className="field" value={form.date} onChange={set("date")} data-testid="custom-date" /></div>
        <div><label className="label">Start time</label><input type="time" className="field" value={form.start_time} onChange={set("start_time")} data-testid="custom-start" /></div>
        <div><label className="label">End time</label><input type="time" className="field" value={form.end_time} onChange={set("end_time")} data-testid="custom-end" /></div>
      </div>
      <div><label className="label">Event address</label><input className="field" value={form.location} onChange={set("location")} data-testid="custom-address" /></div>
      <div className="grid sm:grid-cols-3 gap-4">
        <div><label className="label">Estimated attendance</label><input type="number" min="0" className="field" value={form.guests} onChange={set("guests")} data-testid="custom-attendance" /></div>
        <div><label className="label">People wanting painting</label><input type="number" min="0" className="field" value={form.faces_expected} onChange={set("faces_expected")} data-testid="custom-faces" /></div>
        <div>
          <label className="label">Indoor or outdoor</label>
          <select className="field" value={form.indoor_outdoor} onChange={set("indoor_outdoor")} data-testid="custom-io">
            <option value="">Choose…</option><option>Indoor</option><option>Outdoor</option><option>Both</option>
          </select>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div><label className="label">Theme</label><input className="field" value={form.theme} onChange={set("theme")} data-testid="custom-theme" /></div>
        <div><label className="label">Budget (optional)</label><input className="field" value={form.budget} onChange={set("budget")} data-testid="custom-budget" /></div>
      </div>
      <div><label className="label">Additional details</label><textarea rows={4} className="field" value={form.special_instructions} onChange={set("special_instructions")} data-testid="custom-details" /></div>
      <TermsCheck checked={form.agreed_to_terms} onChange={(v) => setForm({ ...form, agreed_to_terms: !!v })} />
      <button type="submit" disabled={submitting} className="btn-pink w-full text-lg disabled:opacity-60" data-testid="custom-submit">
        {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />} Request my custom quote
      </button>
    </form>
  );
}

function Confirmation({ done, onReset }) {
  return (
    <div className="section py-20 max-w-2xl text-center" data-testid="booking-confirmation">
      <div className="card-pop p-10">
        <div className="w-20 h-20 mx-auto rounded-full bg-leaf border-[3px] border-ink flex items-center justify-center mb-5"><CheckCircle2 className="w-11 h-11 text-white" /></div>
        <p className="font-accent text-3xl text-hotpink">Woohoo!</p>
        <h1 className="font-heading text-4xl font-700 mb-3">Request received</h1>
        <p className="text-ink/80 text-lg mb-4">Thanks {done.name}! Your {done.type.toLowerCase()} has been sent to Tay. She’ll review it and get back to you by email to confirm the details.</p>
        <div className="card-soft p-5 text-left inline-block mb-6">
          <p className="text-sm text-ink/60">Reference</p>
          <p className="font-heading text-lg" data-testid="confirmation-ref">{done.id}</p>
          {done.date && <p className="mt-2"><strong>Date:</strong> {done.date}{done.start_time ? ` at ${done.start_time}` : ""}</p>}
          {done.duration && <p><strong>Duration:</strong> {done.duration} hours</p>}
          <p className="mt-2 chip bg-sunshine">Status: Pending Tay’s approval</p>
        </div>
        <div className="flex flex-wrap gap-3 justify-center">
          <Link to="/" className="btn-blue" data-testid="confirmation-home">Back to home</Link>
          <button onClick={onReset} className="btn-white" data-testid="confirmation-another">Make another request <ArrowRight className="w-4 h-4" /></button>
        </div>
      </div>
    </div>
  );
}
